import { Component, HostBinding, Input, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IdentityService } from 'src/app/@core/services/identity.service';
import { getAvatarInitials } from './avatar-initials';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Avatar with the three DGA contexts (criterion 31): image, initials, icon.
 * Custom mode keeps the former behaviour: when there is no image, `fallbackSrc` (the default
 * avatar picture) is shown. Unified Code mode shows initials from `name`, or the person icon.
 * Size and shape come from the parent (classes/styles on <app-avatar>) unless `size` is set.
 */
@Component({
  selector: 'app-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
})
export class AvatarComponent implements OnChanges {
  @Input() src: string | null = null;
  @Input() name: string | null = null;
  @Input() size: AvatarSize | null = null;
  @Input() icon = 'fa-user';
  /** Picture used in Custom mode when `src` is empty or fails to load. */
  @Input() fallbackSrc: string | null = null;

  @HostBinding('attr.role') readonly role = 'img';
  initials = '';
  imageSrc: string | null = null;
  private failed = false;

  constructor(private identityService: IdentityService, private translateService: TranslateService) { }

  @HostBinding('class.uc-avatar') readonly avatarClass = true;
  @HostBinding('class.uc-avatar-sm') get isSm(): boolean { return this.size === 'sm'; }
  @HostBinding('class.uc-avatar-md') get isMd(): boolean { return this.size === 'md'; }
  @HostBinding('class.uc-avatar-lg') get isLg(): boolean { return this.size === 'lg'; }
  @HostBinding('class.uc-avatar-xl') get isXl(): boolean { return this.size === 'xl'; }
  @HostBinding('class.uc-avatar-fallback') get isFallback(): boolean { return !this.imageSrc; }

  @HostBinding('attr.aria-label') get ariaLabel(): string {
    return this.name
      ? this.translateService.instant('unifiedComponents.avatar', { name: this.name })
      : this.translateService.instant('unifiedComponents.avatarGeneric');
  }

  ngOnChanges(): void {
    this.failed = false;
    this.initials = getAvatarInitials(this.name);
    this.imageSrc = this.resolveImage();
  }

  onImageError(): void {
    if (this.failed) {
      this.imageSrc = null;
      return;
    }
    this.failed = true;
    this.imageSrc = this.identityService.isUnifiedCode() ? null : this.fallbackSrc;
  }

  private resolveImage(): string | null {
    if (this.src) return this.src;
    return this.identityService.isUnifiedCode() ? null : this.fallbackSrc;
  }
}
